import React, { useEffect, useMemo, useState } from 'react';
import '../styles/ProductQuickViewModal.css';
import { getVariantDisplayLabel, sortVariantsAscending } from '../utils/productVariantOrdering';

const ProductQuickViewModal = ({ product, onClose, onAddToBooking }) => {
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const activeProduct = product || {};

  const variants = useMemo(() => {
    if (!Array.isArray(activeProduct?.variants)) return [];
    return activeProduct.variants.filter((variant) => variant && variant._id);
  }, [activeProduct]);

  const sortedVariants = useMemo(() => sortVariantsAscending(variants), [variants]);

  const hasSelectableVariants =
    activeProduct?.hasVariants === true ||
    Number(activeProduct?.variantCount) > 1 ||
    sortedVariants.length > 1;

  useEffect(() => {
    if (sortedVariants.length > 0) {
      setSelectedVariantId(String(sortedVariants[0]._id));
      return;
    }
    setSelectedVariantId('');
  }, [activeProduct, sortedVariants]);

  const selectedVariant =
    sortedVariants.find((variant) => String(variant._id) === String(selectedVariantId)) ||
    sortedVariants[0] ||
    null;

  const displayImages = useMemo(() => {
    const variantImages = Array.isArray(selectedVariant?.variantImages)
      ? selectedVariant.variantImages.filter(Boolean)
      : [];
    const baseImages = Array.isArray(product?.images)
      ? product.images.filter(Boolean)
      : [];
    const fallbackImage = product?.imageUrl ? [product.imageUrl] : [];
    const images = variantImages.length > 0 ? variantImages : baseImages.length > 0 ? baseImages : fallbackImage;
    return images;
  }, [product, selectedVariant]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [product?._id, selectedVariant?._id]);

  const activeImage = displayImages[selectedImageIndex] || displayImages[0] || '/default-product.png';

  const displayPrice =
    typeof selectedVariant?.price === 'number'
      ? selectedVariant.price
      : typeof product.price === 'number'
        ? product.price
        : 0;

  const selectedStock =
    typeof selectedVariant?.stock === 'number'
      ? selectedVariant.stock
      : typeof activeProduct?.stock === 'number'
        ? activeProduct.stock
        : null;

  const isOutOfStock =
    activeProduct.inStock === false || (typeof selectedStock === 'number' && selectedStock <= 0);

  if (!product) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAddToBooking = () => {
    if (onAddToBooking) {
      onAddToBooking(
        product,
        hasSelectableVariants && selectedVariant ? selectedVariant : null,
      );
    }
    onClose();
  };

  return (
    <div className="product-quick-view-overlay" onClick={handleBackdropClick}>
      <div className="product-quick-view-modal">
        {/* Close button */}
        <button className="quick-view-close-btn" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="quick-view-content">
          {/* Product Image */}
          <div className="quick-view-image-section">
            <img
              src={activeImage}
              alt={product.name}
              className="quick-view-product-image"
            />
            {displayImages.length > 1 && (
              <div className="quick-view-image-thumb-row">
                {displayImages.map((image, index) => {
                  const isActive = index === selectedImageIndex;
                  return (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      className={`quick-view-image-thumb${isActive ? ' active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img src={image} alt={`${product.name} ${index + 1}`} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="quick-view-details-section">
            <div className="quick-view-header">
              {product.brand && (
                <span className="quick-view-brand">{product.brand}</span>
              )}
              <h2 className="quick-view-title">{product.name}</h2>
              <p className="quick-view-price">
                ${displayPrice.toFixed(2)}
              </p>
            </div>

            {hasSelectableVariants && sortedVariants.length > 0 && (
              <div className="quick-view-usage">
                <h3>Options</h3>
                <div className="quick-view-option-row">
                  {sortedVariants.map((variant) => {
                    const isSelected = String(selectedVariant?._id) === String(variant._id);

                    return (
                      <button
                        key={variant._id}
                        type="button"
                        className={`quick-view-option-chip${isSelected ? ' active' : ''}`}
                        onClick={() => setSelectedVariantId(String(variant._id))}
                      >
                        {getVariantDisplayLabel(variant)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.description && (
              <div className="quick-view-description">
                <h3>Description</h3>
                <p>{product.description}</p>
              </div>
            )}

            {product.ingredients && (
              <div className="quick-view-ingredients">
                <h3>Key Ingredients</h3>
                <p>{product.ingredients}</p>
              </div>
            )}

            {product.benefits && (
              <div className="quick-view-benefits">
                <h3>Benefits</h3>
                <p>{product.benefits}</p>
              </div>
            )}

            {product.howToUse && (
              <div className="quick-view-usage">
                <h3>How to Use</h3>
                <p>{product.howToUse}</p>
              </div>
            )}

            {isOutOfStock && (
              <div className="quick-view-out-of-stock">
                <span>⚠️ Currently Out of Stock</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="quick-view-actions">
              <button 
                className="quick-view-add-btn"
                onClick={handleAddToBooking}
                disabled={isOutOfStock}
              >
                {isOutOfStock ? 'Out of Stock' : 'Add to Booking'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickViewModal;
